import { ArrivalIdentity, ArrivalTouch, LastArrival } from 'core/analytics/entities/Arrival';
import { SnitcherEventNames } from 'core/analytics/entities/Snitcher';
import { ArrivalService } from 'core/analytics/interfaces';
import { HOUR } from 'core/common/constants/durations';
import { normalizeError } from 'core/common/errors';
import { HttpClient } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { StorageInterface } from 'core/common/services/storage';
import { isEqual } from 'core/common/utils/isEqual';
import { UtmTags } from 'core/user/entities';
import { UtmTagsService } from 'core/user/services';
import { ArrivalEventDto } from './dto';

export class ArrivalClientService implements ArrivalService {
  private readonly storageKey = 'nebula_last_arrival';

  private readonly url = new URL(window.location.href);

  private isArrivalTracked = false;

  constructor(
    private readonly utmTagsService: UtmTagsService,
    private readonly httpClient: HttpClient,
    private readonly localStorage: StorageInterface,
    private readonly logger: Logger,
    private readonly identity: ArrivalIdentity,
  ) {}

  trackArrival(locale: string) {
    if (this.isArrivalTracked) return;

    this.isArrivalTracked = true;

    const touch: ArrivalTouch = {
      utmTags: this.utmTagsService.getQueryUtmTags(),
      referrerOrigin: this.getReferrerOrigin(),
    };

    if (!this.shouldSendArrival(touch)) return;

    this.saveLastArrival(touch);
    void this.sendArrival(touch.utmTags, locale);
  }

  private getReferrerOrigin(): string {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return '';
    }
  }

  private shouldSendArrival({ utmTags, referrerOrigin }: ArrivalTouch): boolean {
    if (referrerOrigin === this.url.origin) return false;

    const lastArrival = this.getLastArrival();

    if (!lastArrival) return true;

    if (Object.keys(utmTags).length) {
      return !isEqual(utmTags, lastArrival.utmTags);
    }

    return referrerOrigin !== lastArrival.referrerOrigin;
  }

  private getLastArrival(): LastArrival | null {
    try {
      const lastArrival = this.localStorage.get<LastArrival>(this.storageKey);

      return lastArrival && this.isLastArrivalFresh(lastArrival) ? lastArrival : null;
    } catch {
      return null;
    }
  }

  private isLastArrivalFresh({ sentAt }: LastArrival): boolean {
    return Date.now() - sentAt < 5 * HOUR;
  }

  private saveLastArrival(touch: ArrivalTouch) {
    try {
      this.localStorage.set(this.storageKey, { ...touch, sentAt: Date.now() });
    } catch (err) {
      this.logger.error(normalizeError(err));
    }
  }

  private async sendArrival(utmTags: UtmTags, locale: string) {
    try {
      await this.httpClient.post('/snitcher/event', {
        adapter: 'fetch',
        fetchOptions: { keepalive: true },
        headers: { locale },
        data: this.createArrivalEvent(utmTags),
      });
    } catch (err) {
      this.logger.error(normalizeError(err));
    }
  }

  private createArrivalEvent(utmTags: UtmTags): ArrivalEventDto {
    return {
      event_name: SnitcherEventNames.ARRIVAL,
      event_time: Date.now(),
      split_id: this.identity.splitId,
      utm_params: utmTags,
      properties: {
        host: this.url.host,
        path: this.url.pathname,
        referrer: document.referrer,
        user_id: this.getUserId(),
        is_tester: this.identity.isTester,
      },
    };
  }

  private getUserId(): string | null {
    return this.httpClient.getHeaders().User || null;
  }
}
